import {
  VestingGraphPoint,
  VestingGraphVestingPeriod,
} from '../../../../../hooks/vesting-task/use-vesting-graph';
import { calendarTimeOf, getTimestampPosition } from './vesting-graph.geometry';
import {
  HoveredVestingPeriod,
  LayoutChartPoint,
  VestingGraphSeriesPoint,
} from './vesting-graph.types';

const isTimestampInVestingPeriod = ({
  isFirstPeriod,
  periodEnd,
  periodStart,
  timestamp,
}: {
  isFirstPeriod: boolean;
  periodEnd: number;
  periodStart: number;
  timestamp: number;
}) => {
  if (isFirstPeriod) return timestamp >= periodStart && timestamp <= periodEnd;

  return timestamp > periodStart && timestamp <= periodEnd;
};

export const resolveHoveredVestingPeriod = ({
  chartPoints,
  domainMax,
  domainMin,
  timestamp,
  vestingPeriods,
}: {
  chartPoints: LayoutChartPoint[];
  domainMax: number;
  domainMin: number;
  /** Calendar time of the hovered bar (not the equal-column layout x). */
  timestamp: null | number;
  vestingPeriods?: VestingGraphVestingPeriod[];
}): HoveredVestingPeriod | null => {
  if (timestamp === null || !vestingPeriods?.length) return null;

  const sortedPeriods = [...vestingPeriods].sort((first, second) => first.index - second.index);

  const period = sortedPeriods.find((candidate, index) =>
    isTimestampInVestingPeriod({
      isFirstPeriod: index === 0,
      periodEnd: new Date(candidate.endDate).getTime(),
      periodStart: new Date(candidate.startDate).getTime(),
      timestamp,
    }),
  );

  if (!period) return null;

  const periodStartTimestamp = new Date(period.startDate).getTime();
  const periodEndTimestamp = new Date(period.endDate).getTime();
  const isFirstPeriod = sortedPeriods[0]?.index === period.index;
  const inPeriodIndexes = chartPoints.reduce<number[]>((indexes, point, index) => {
    if (
      isTimestampInVestingPeriod({
        isFirstPeriod,
        periodEnd: periodEndTimestamp,
        periodStart: periodStartTimestamp,
        timestamp: calendarTimeOf(point),
      })
    ) {
      indexes.push(index);
    }

    return indexes;
  }, []);

  const positionOf = (value: number) =>
    getTimestampPosition({ maxTimestamp: domainMax, minTimestamp: domainMin, timestamp: value });

  if (!inPeriodIndexes.length) {
    // No payment column in this window — fall back to the full plot span for the first
    // period, otherwise skip drawing a bracket that cannot align to a column edge.
    if (!isFirstPeriod) return null;

    const leftPercent = positionOf(domainMin);

    return {
      endTimestamp: domainMax,
      index: period.index,
      leftPercent,
      periodStartTimestamp,
      startTimestamp: domainMin,
      widthPercent: Math.max(positionOf(domainMax) - leftPercent, 0),
    };
  }

  const firstIndex = inPeriodIndexes[0]!;
  const lastIndex = inPeriodIndexes[inPeriodIndexes.length - 1]!;
  const firstPoint = chartPoints[firstIndex]!;
  const lastPoint = chartPoints[lastIndex]!;
  const firstHalf = firstPoint.slotHalfWidth ?? 0;
  const lastHalf = lastPoint.slotHalfWidth ?? 0;

  // Adjacent periods share the gutter between columns (last+half === next-half).
  const startTimestamp = isFirstPeriod ? domainMin : firstPoint.timestamp - firstHalf;
  const endTimestamp = lastPoint.timestamp + lastHalf;
  const leftPercent = positionOf(startTimestamp);

  return {
    endTimestamp,
    index: period.index,
    leftPercent,
    periodStartTimestamp,
    startTimestamp,
    widthPercent: Math.max(positionOf(endTimestamp) - leftPercent, 0),
  };
};

/**
 * Shares released inside the allocation window up to this bar. Dynamic tooltips talk about
 * the named period, so a frequency step alone would under-report once a period pays more
 * than once — and would still show the previous period's step while the next window is open.
 */
export const resolveAllocationPeriodVestingStepShares = ({
  points,
  pointTimestamp,
  vestingPeriods,
}: {
  points: VestingGraphPoint[];
  pointTimestamp: number;
  vestingPeriods?: VestingGraphVestingPeriod[];
}): null | number => {
  if (!vestingPeriods || !vestingPeriods.some((period) => period.planAllocation !== undefined)) {
    return null;
  }

  const sortedPeriods = [...vestingPeriods].sort((first, second) => first.index - second.index);
  const firstPeriodIndex = sortedPeriods[0]?.index;
  const periodIndex = sortedPeriods.findIndex((candidate) =>
    isTimestampInVestingPeriod({
      isFirstPeriod: candidate.index === firstPeriodIndex,
      periodEnd: new Date(candidate.endDate).getTime(),
      periodStart: new Date(candidate.startDate).getTime(),
      timestamp: pointTimestamp,
    }),
  );
  if (periodIndex < 0) return null;

  const period = sortedPeriods[periodIndex]!;
  const periodStart = new Date(period.startDate).getTime();
  const periodEnd = new Date(period.endDate).getTime();
  const isFirstPeriod = period.index === firstPeriodIndex;

  return points.reduce((sum, point) => {
    const time = new Date(point.date).getTime();
    if (time > pointTimestamp) return sum;
    if (
      !isTimestampInVestingPeriod({
        isFirstPeriod,
        periodEnd,
        periodStart,
        timestamp: time,
      })
    ) {
      return sum;
    }

    return sum + point.periodShares;
  }, 0);
};

const hasMilestoneColumnFill = (point: VestingGraphSeriesPoint) =>
  point.vestedPercent !== null || point.projectedPercent !== null;

/**
 * Target columns are drawn as a start→end ramp, but the hover is the named target —
 * always the closing totals, never the previous milestone's holding at the left edge.
 */
export const resolveMilestoneColumnHoverPoint = ({
  chartData,
  point,
}: {
  chartData: VestingGraphSeriesPoint[];
  point: VestingGraphSeriesPoint;
}): null | VestingGraphSeriesPoint => {
  if (!point.milestoneName || point.isEventColumn) return null;

  const columnPoints = chartData.filter(
    (candidate) =>
      candidate.milestoneName === point.milestoneName &&
      candidate.calendarTimestamp === point.calendarTimestamp &&
      !candidate.isEventColumn &&
      hasMilestoneColumnFill(candidate),
  );

  if (!columnPoints.length) return null;

  return columnPoints.reduce((latest, candidate) =>
    candidate.timestamp > latest.timestamp ? candidate : latest,
  );
};
